import { numeralFormatter } from 'ns_libs/formatter';
import {
    IProFormaSourceTableRow, IProFormaUses, ISourcesSummary, IUsesSummary,
} from 'views/ProFormaTable/types';
import { RowValue } from '../../ExecutiveSummaryGenericTable/types';
import { DevelopmentDynamicRow, DevelopmentResolver, DevelopmentUseGroup } from '../../layout/development';

const ROOT_GROUP = 'root';
const PERCENTAGE_PRECISION = '0.0000';

const useRow = (use: IProFormaUses, totalUsesAmount: number): DevelopmentDynamicRow => ({
    id: `use-${use.id}`,
    label: use.description || '',
    cells: [
        {
            value: totalUsesAmount !== 0 && use.amount ? numeralFormatter(use.amount / totalUsesAmount, PERCENTAGE_PRECISION) : '-',
            format: 'percentage',
        },
        { value: use.costPerGrossSquareFootage || '-', format: 'currency-cents' },
        { value: use.costPerResidentialUnit || '-', format: 'currency' },
        { value: use.amount || '-', format: 'currency' },
    ],
});

const sourceRow = (source: IProFormaSourceTableRow): DevelopmentDynamicRow => ({
    id: `source-${source.id}`,
    label: source.name,
    cells: [
        { value: source.sourcesPercentage || '-', format: 'percentage' },
        { value: source.costPerGrossSquareFootage || '-', format: 'currency-cents' },
        { value: source.costPerResidentialUnit || '-', format: 'currency' },
        { value: source.amount || '-', format: 'currency' },
    ],
});

const groupUses = (usesSummary: IUsesSummary | undefined): DevelopmentUseGroup[] => {
    const uses = usesSummary?.uses;
    if (!uses) return [];

    const totalUsesAmount = usesSummary.totals.amount || 0;
    const usesByParent = new Map<number | typeof ROOT_GROUP, IProFormaUses[]>();
    uses.forEach(use => {
        const parentId = use.parent?.id || ROOT_GROUP;
        const siblings = usesByParent.get(parentId);
        if (siblings) siblings.push(use);
        else usesByParent.set(parentId, [use]);
    });

    return Array.from(usesByParent.entries()).map(([parentId, groupedUses]) => ({
        id: `uses-section-${parentId}`,
        title: parentId === ROOT_GROUP ? 'Uses' : groupedUses[0]?.parent?.nodeName || '',
        rows: groupedUses.map(use => useRow(use, totalUsesAmount)),
    }));
};

const totalCell = (value: number | null | undefined, format: RowValue['format']) => (): RowValue => ({
    value: value || '-',
    format,
});

export const proFormaDevelopmentResolver = (
    usesSummary: IUsesSummary | undefined,
    sourcesSummary: ISourcesSummary | undefined,
): DevelopmentResolver => ({
    hasData: () => !!usesSummary?.uses || !!sourcesSummary?.sources,
    hasUses: () => !!usesSummary?.uses,
    hasSources: () => !!sourcesSummary?.sources,
    useGroups: () => groupUses(usesSummary),
    sources: () => (sourcesSummary?.sources ?? []).map(sourceRow),

    'totalUses.percent': () => ({ value: 1, format: 'percentage' }),
    'totalUses.perBuiltSf': totalCell(usesSummary?.totals.costPerGrossSquareFootage, 'currency-cents'),
    'totalUses.perUnit': totalCell(usesSummary?.totals.costPerResidentialUnit, 'currency'),
    'totalUses.amount': totalCell(usesSummary?.totals.amount, 'currency'),

    'totalSources.percent': () => ({ value: 1, format: 'percentage' }),
    'totalSources.perBuiltSf': totalCell(sourcesSummary?.totals.costPerGrossSquareFootage, 'currency-cents'),
    'totalSources.perUnit': totalCell(sourcesSummary?.totals.costPerResidentialUnit, 'currency'),
    'totalSources.amount': totalCell(sourcesSummary?.totals.amount, 'currency'),
});
